import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { describeError } from "@/lib/api";
import { StatusBadge } from "@/components/common/StatusBadge";
import { LoadingState } from "@/components/common/EmptyState";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { fmtDate } from "@/lib/helpers";
import { toast } from "sonner";
import { ArrowLeft, Phone, Mail, MapPin, Briefcase, Gavel, Trash2, Archive, ArchiveRestore } from "lucide-react";

export default function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const load = useCallback(() => {
    api.get(`/clients/${id}`).then(({ data }) => setData(data)).catch(() => navigate("/clients"));
  }, [id, navigate]);
  useEffect(() => { load(); }, [load]);

  if (!data) return <LoadingState />;
  const { client, cases, hearings } = data;
  const hasCases = cases.length > 0;

  const doDelete = async () => {
    try {
      await api.delete(`/clients/${id}`);
      toast.success("Client deleted");
      navigate("/clients");
    } catch (err) {
      toast.error(describeError(err, "Could not delete the client."));
    } finally {
      setConfirmDelete(false);
    }
  };

  const doArchive = async () => {
    try {
      await api.post(`/clients/${id}/${client.archived ? "unarchive" : "archive"}`);
      toast.success(client.archived ? "Client restored" : "Client archived");
      load();
    } catch (err) { toast.error(describeError(err, "Could not update the client.")); }
  };

  return (
    <div data-testid="client-detail-page">
      <button onClick={() => navigate("/clients")} className="mb-4 flex items-center gap-1.5 text-sm text-muted-foreground hover:text-navy">
        <ArrowLeft className="h-4 w-4" /> Back to clients
      </button>

      <Card className="mb-6 border-border p-6 shadow-sm">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
          <Avatar className="h-16 w-16"><AvatarFallback className="bg-navy text-lg text-white">{client.name.split(" ").slice(0, 2).map((w) => w[0]).join("")}</AvatarFallback></Avatar>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h1 className="font-display text-3xl tracking-tight text-navy">{client.name}</h1>
              {client.archived && <span className="rounded-full border border-amber-200 bg-amber-50 px-2.5 py-0.5 text-xs font-medium text-amber-700" data-testid="client-archived-badge">Archived</span>}
            </div>
            <p className="text-sm text-muted-foreground">{client.client_code}</p>
          </div>
          <div className="space-y-1.5 text-sm text-muted-foreground">
            {client.mobile && <p className="flex items-center gap-2"><Phone className="h-3.5 w-3.5" /> {client.mobile}</p>}
            {client.email && <p className="flex items-center gap-2"><Mail className="h-3.5 w-3.5" /> {client.email}</p>}
            {client.address && <p className="flex items-center gap-2"><MapPin className="h-3.5 w-3.5" /> {client.address}</p>}
          </div>
        </div>
        {client.notes && <p className="mt-4 rounded-md bg-secondary/50 p-3 text-sm text-muted-foreground">{client.notes}</p>}
        <div className="mt-4 flex flex-wrap gap-2 border-t border-border pt-4">
          <Button variant="outline" size="sm" onClick={doArchive} data-testid="client-archive-btn">
            {client.archived ? <><ArchiveRestore className="mr-1.5 h-3.5 w-3.5" />Restore Client</> : <><Archive className="mr-1.5 h-3.5 w-3.5" />Archive Client</>}
          </Button>
          <Button variant="outline" size="sm" onClick={() => setConfirmDelete(true)} data-testid="client-delete-btn" className="text-rose-600 hover:bg-rose-50">
            <Trash2 className="mr-1.5 h-3.5 w-3.5" />Delete Client
          </Button>
          {hasCases && <p className="self-center text-xs text-muted-foreground">This client has {cases.length} case{cases.length === 1 ? "" : "s"} — archive to preserve history.</p>}
        </div>
      </Card>

      <Tabs defaultValue="cases">
        <TabsList className="mb-4 bg-secondary/60">
          <TabsTrigger value="cases" data-testid="client-tab-cases"><Briefcase className="mr-1.5 h-3.5 w-3.5" />Cases ({cases.length})</TabsTrigger>
          <TabsTrigger value="hearings" data-testid="client-tab-hearings"><Gavel className="mr-1.5 h-3.5 w-3.5" />Hearings ({hearings.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="cases">
          <Card className="divide-y divide-border border-border shadow-sm">
            {cases.length === 0 && <div className="p-8 text-center text-sm text-muted-foreground">No cases for this client.</div>}
            {cases.map((c) => (
              <button key={c.id} onClick={() => navigate(`/cases/${c.id}`)} className="flex w-full items-center justify-between p-4 text-left hover:bg-secondary/40">
                <div><p className="font-medium text-navy">{c.title}</p><p className="text-xs text-muted-foreground">{c.case_number} · {c.court}</p></div>
                <StatusBadge status={c.status} />
              </button>
            ))}
          </Card>
        </TabsContent>
        <TabsContent value="hearings">
          <Card className="divide-y divide-border border-border shadow-sm">
            {hearings.length === 0 && <div className="p-8 text-center text-sm text-muted-foreground">No hearings.</div>}
            {hearings.sort((a, b) => (b.date || "").localeCompare(a.date || "")).map((h) => (
              <div key={h.id} className="flex items-center justify-between p-4">
                <div><p className="text-sm font-medium text-navy">{h.purpose || "Hearing"}</p><p className="text-xs text-muted-foreground">{fmtDate(h.date)} · {h.time} · {h.court}</p></div>
                <StatusBadge status={h.status} />
              </div>
            ))}
          </Card>
        </TabsContent>
      </Tabs>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this client?</AlertDialogTitle>
            <AlertDialogDescription>
              {hasCases
                ? "This client has associated cases and cannot be permanently deleted. Please archive the client instead to preserve all case history."
                : `"${client.name}" will be permanently removed. This cannot be undone.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="client-delete-dismiss">Cancel</AlertDialogCancel>
            {hasCases ? (
              <AlertDialogAction onClick={() => { setConfirmDelete(false); doArchive(); }} data-testid="client-archive-instead">Archive instead</AlertDialogAction>
            ) : (
              <AlertDialogAction onClick={doDelete} data-testid="client-delete-confirm" className="bg-rose-600 hover:bg-rose-700">Delete</AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
